import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight, FiEdit2, FiCheck, FiX } from "react-icons/fi";
import { setModalState, closeModalWithResult, modalViewsConst } from "../../store/modalSlice";
import ModalBase from "./ModalBase";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import type { UserProjectTask } from "../../services/projectService";
import { updateProjectTask } from "../../store/projectChatSlice";
import type { AppDispatch, RootState } from "../../store";

interface ProjectTasksModalProps {
  onClose: () => void;
}

const ProjectTasksModal: React.FC<ProjectTasksModalProps> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const properties = useSelector((state: RootState) => state.modalState.properties);
  const initialTasks: UserProjectTask[] = properties?.tasks ?? [];
  const projectTitle: string = properties?.projectTitle ?? "Project";
  const projectId: string = properties?.projectId ?? "";

  const [localTasks, setLocalTasks] = useState<UserProjectTask[]>(initialTasks);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleClose = () => {
    dispatch(closeModalWithResult(null));
    onClose();
  };

  const handleTaskClick = (task: UserProjectTask) => {
    if (task.completedTask || editingId === task.id) return;
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.projectChat,
      properties: {
        __modalSize: "2xl",
        projectId,
        projectTitle,
        projectTaskId: task.id,
        initialInputValue: `${task.title}: ${task.description}`,
      },
    }));
  };

  const handleEditClick = (e: React.MouseEvent, task: UserProjectTask) => {
    e.stopPropagation();
    setEditingId(task.id);
    setEditTitle(task.title);
    setEditDescription(task.description);
    setSaveError(null);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
    setSaveError(null);
  };

  const handleSaveEdit = async (e: React.MouseEvent, task: UserProjectTask) => {
    e.stopPropagation();
    if (!editTitle.trim()) return;
    setSavingId(task.id);
    setSaveError(null);
    const result = await dispatch(
      updateProjectTask({ id: task.id, title: editTitle.trim(), description: editDescription.trim() })
    );
    if (updateProjectTask.fulfilled.match(result)) {
      setLocalTasks((prev) => prev.map((t) => (t.id === result.payload.id ? result.payload : t)));
      setEditingId(null);
    } else {
      setSaveError("Failed to save. Please try again.");
    }
    setSavingId(null);
  };

  return (
    <ModalBase
      title="Project Tasks"
      icon={<BriefcaseIcon className="w-5 h-5 stroke-eai-primary-accent" />}
      onClose={handleClose}
      size="md"
      footer={
        <button
          onClick={handleClose}
          className="border border-gray-200 rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      }
    >
      <div className="flex flex-col gap-2 mb-4">
        <p className="text-sm text-gray-500 font-Inter">
          AI-suggested tasks for <span className="font-semibold text-gray-700">{projectTitle}</span>
        </p>
      </div>

      {localTasks.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          No tasks found for this project.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {localTasks.map((task, index) => {
            const isComplete = task.completedTask === true;
            const isEditing = editingId === task.id;
            const isSaving = savingId === task.id;

            return (
              <div
                key={task.id}
                onClick={() => handleTaskClick(task)}
                className={`border rounded-lg p-4 flex gap-4 items-start transition-colors group ${
                  isComplete
                    ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
                    : isEditing
                    ? "border-eai-primary-accent bg-white cursor-default"
                    : "border-gray-200 cursor-pointer hover:border-gray-400 hover:bg-gray-50"
                }`}
              >
                {/* Number / check circle */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isComplete ? "bg-green-500" : "bg-eai-primary-accent"}`}>
                  {isComplete ? (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        maxLength={100}
                        className="w-full text-sm font-semibold text-gray-900 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:border-eai-primary-accent"
                        placeholder="Task title"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        className="w-full text-sm text-gray-600 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:border-eai-primary-accent resize-none"
                        placeholder="Task description"
                      />
                      {saveError && <p className="text-xs text-red-500">{saveError}</p>}
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          <FiX className="w-3 h-3" />
                          Cancel
                        </button>
                        <button
                          onClick={(e) => handleSaveEdit(e, task)}
                          disabled={isSaving || !editTitle.trim()}
                          className="flex items-center gap-1 px-3 py-1 bg-eai-primary-accent text-white text-xs rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiCheck className="w-3 h-3" />
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-semibold ${isComplete ? "line-through text-gray-400" : "text-gray-900"}`}>
                          {task.title}
                        </span>
                        {isComplete && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Completed
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 leading-relaxed">{task.description}</div>
                    </>
                  )}
                </div>

                {/* Right-side actions */}
                {!isComplete && !isEditing && (
                  <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                    <button
                      onClick={(e) => handleEditClick(e, task)}
                      className="p-1 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
                      title="Edit task"
                    >
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <FiArrowRight className="w-4 h-4 text-gray-300 group-hover:text-eai-primary-accent transition-colors" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ModalBase>
  );
};

export default ProjectTasksModal;
